import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Snowflake, Sparkles, ArrowRight, Loader2, ArrowLeft, Mail, Building2,
  CheckCircle, ShieldCheck, FileText, Inbox, AlertTriangle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

// Same 10 base questions as the health score, plus 3 deeper ones at the end
const QUESTIONS = [
  { key: 'monthly_spend', title: "What's your approximate monthly Snowflake spend?", sub: 'Best estimate is fine — we use ranges.', options: [
    { value: 'lt_5k', label: 'Less than $5K' },
    { value: '5_25k', label: '$5K – $25K' },
    { value: '25_100k', label: '$25K – $100K' },
    { value: '100k_plus', label: '$100K or more' },
  ] },
  { key: 'warehouse_count', title: 'How many warehouses are running in your account?', sub: 'Including dev/staging warehouses.', options: [
    { value: '1-5', label: '1 – 5' },
    { value: '6-15', label: '6 – 15' },
    { value: '16-50', label: '16 – 50' },
    { value: '50+', label: '50+' },
  ] },
  { key: 'always_on_warehouses', title: 'How many warehouses run 24/7 without auto-suspend?', sub: 'This is the #1 source of Snowflake waste.', options: [
    { value: 'none', label: 'None — everything auto-suspends' },
    { value: '1-2', label: '1 – 2' },
    { value: '3-5', label: '3 – 5' },
    { value: 'unsure', label: "I'm honestly not sure" },
  ] },
  { key: 'auto_suspend_timeout', title: 'What is your default auto-suspend timeout?', sub: 'Snowflake bills per-second after a 60s warm-up — long timeouts = waste.', options: [
    { value: '60s_or_less', label: '60 seconds or less' },
    { value: '5_min', label: '5 minutes' },
    { value: '10_min', label: '10 minutes' },
    { value: '15_plus_min', label: '15+ minutes' },
    { value: 'none', label: 'Auto-suspend is disabled' },
  ] },
  { key: 'largest_warehouse_size', title: 'What is the size of your LARGEST warehouse?', sub: 'Each tier-up doubles credit consumption.', options: [
    { value: 'xs', label: 'X-Small' },
    { value: 's', label: 'Small' },
    { value: 'm', label: 'Medium' },
    { value: 'l', label: 'Large' },
    { value: 'xl_plus', label: 'X-Large or larger' },
  ] },
  { key: 'short_query_pct', title: 'What % of your queries finish in less than 10 seconds?', sub: 'High % + Large warehouse = over-provisioning.', options: [
    { value: 'lt_25', label: 'Less than 25%' },
    { value: '25_50', label: '25 – 50%' },
    { value: '50_75', label: '50 – 75%' },
    { value: '75_plus', label: '75% or more' },
  ] },
  { key: 'multi_cluster', title: 'Are your warehouses configured as multi-cluster?', sub: 'Multi-cluster is great for concurrency spikes — wasteful when always-on.', options: [
    { value: 'always', label: 'Yes, always-on multi-cluster' },
    { value: 'some', label: 'Some — auto-scale enabled' },
    { value: 'no', label: 'No, single-cluster only' },
    { value: 'unsure', label: 'Not sure' },
  ] },
  { key: 'storage_size', title: 'How much data are you storing in Snowflake?', sub: 'Total across all databases.', options: [
    { value: 'lt_1tb', label: 'Less than 1 TB' },
    { value: '1_10tb', label: '1 – 10 TB' },
    { value: '10_50tb', label: '10 – 50 TB' },
    { value: '50tb_plus', label: '50 TB or more' },
  ] },
  { key: 'time_travel_retention', title: 'What is your typical time-travel retention?', sub: 'Each extra day proportionally multiplies storage cost.', options: [
    { value: '1_day', label: '1 day (default)' },
    { value: '7_days', label: '7 days' },
    { value: '30_days', label: '30 days' },
    { value: '90_days', label: '90 days (Enterprise max)' },
  ] },
  { key: 'review_cadence', title: 'How often does your team review Snowflake costs?', sub: "What you don't measure, you can't improve.", options: [
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'rarely', label: 'Rarely / never' },
  ] },
  // ─── Extra context questions for the deeper audit ────────────────────────
  { key: 'team_size', title: 'How big is the team using Snowflake?', sub: 'We use this to tailor the team-process recommendations.', options: [
    { value: '1-5', label: '1 – 5 people' },
    { value: '6-20', label: '6 – 20 people' },
    { value: '21-100', label: '21 – 100 people' },
    { value: '100+', label: '100+ people' },
  ] },
  { key: 'primary_use_case', title: 'What is your PRIMARY use case for Snowflake?', sub: 'Different use cases have different waste patterns.', options: [
    { value: 'bi_reporting', label: 'BI / Reporting / Dashboards' },
    { value: 'etl_data_eng', label: 'ETL / Data Engineering pipelines' },
    { value: 'data_science_ml', label: 'Data Science / ML feature engineering' },
    { value: 'embedded_analytics', label: 'Embedded analytics in customer products' },
    { value: 'mixed', label: 'Mixed — multiple use cases' },
  ] },
  { key: 'biggest_concern', title: "What's your BIGGEST cost concern right now?", sub: "We'll prioritize the audit findings around this.", options: [
    { value: 'bill_too_high', label: 'The bill is just too high' },
    { value: 'no_visibility', label: "I don't have visibility into who's spending what" },
    { value: 'spike_panic', label: 'Surprise spikes / runaway queries' },
    { value: 'team_chargeback', label: 'Need to attribute costs back to teams' },
    { value: 'audit_compliance', label: 'Audit / compliance / SOC 2 requirements' },
  ] },
];

const SnowflakeAuditTool = () => {
  // -1 = email gate, 0..N-1 = questions, N = sending, N+1 = result
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState({});
  const [profile, setProfile] = useState({ email: '', name: '', company: '' });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    document.title = 'Free Snowflake Cost Audit (Email Report) | CARTIEAI';
    const meta = document.querySelector('meta[name="description"]');
    const desc = "Get a free deeper Snowflake cost audit — 13 questions, full action plan, emailed to your inbox in under 60 seconds. Built by FinOps practitioners.";
    if (meta) meta.setAttribute('content', desc);
  }, []);

  const progressPct = step < 0 ? 0 : step >= QUESTIONS.length ? 100 : Math.round(((step + 1) / QUESTIONS.length) * 100);

  const onPick = async (key, value) => {
    const next = { ...answers, [key]: value };
    setAnswers(next);
    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      // Last answer — submit
      setSubmitting(true);
      setStep(QUESTIONS.length);
      try {
        const payload = { ...next, email: profile.email, name: profile.name || null, company: profile.company || null };
        const { data } = await axios.post(`${API}/api/snowflake-audit-tool/generate`, payload);
        setResult(data);
        setStep(QUESTIONS.length + 1);
      } catch (e) {
        toast.error("Couldn't generate your audit. Please try again.");
        setStep(QUESTIONS.length - 1);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const onBack = () => { if (step > -1) setStep(step - 1); };

  const submitEmailGate = (e) => {
    e?.preventDefault?.();
    if (!/^\S+@\S+\.\S+$/.test(profile.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setStep(0);
  };

  // ─── EMAIL GATE ─────────────────────────────────────────────────────────────
  if (step === -1) {
    return (
      <div className="min-h-screen bg-slate-950 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
        <Header />
        <main className="max-w-2xl mx-auto px-4 py-10 sm:py-16" data-testid="audit-tool-email-gate">
          <Link to="/" className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1 mb-6">
            <ArrowLeft className="w-3 h-3" /> Back to home
          </Link>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 mb-6">
            <FileText className="w-3 h-3" /> 1-time deeper audit · Free
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            Get your <span className="text-emerald-400">free Snowflake Cost Audit</span> emailed in 60 seconds.
          </h1>
          <p className="text-slate-300 leading-relaxed mb-8">
            13 quick questions. Full action plan with all findings (not just top 3),
            severity ranking, fix instructions, and your monthly waste estimate —
            delivered straight to your inbox.
          </p>

          <form onSubmit={submitEmailGate} className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6 space-y-4 backdrop-blur">
            <div>
              <Label htmlFor="audit-email" className="text-slate-300 mb-1.5 block">Work email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="audit-email"
                  type="email"
                  placeholder="you@company.com"
                  required
                  value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  className="pl-9 bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-400"
                  data-testid="audit-tool-email-input"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="audit-name" className="text-slate-300 mb-1.5 block">Your name</Label>
                <Input
                  id="audit-name"
                  type="text"
                  placeholder="Lakshmi"
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  className="bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-400"
                  data-testid="audit-tool-name-input"
                />
              </div>
              <div>
                <Label htmlFor="audit-company" className="text-slate-300 mb-1.5 block">Company</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="audit-company"
                    type="text"
                    placeholder="Acme Inc."
                    value={profile.company}
                    onChange={(e) => setProfile({ ...profile, company: e.target.value })}
                    className="pl-9 bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-400"
                    data-testid="audit-tool-company-input"
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
              data-testid="audit-tool-start-btn"
            >
              Start my free audit
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            <p className="text-[11px] text-slate-400 text-center pt-1">
              By starting the audit you agree to receive <strong>The FinOps Brief</strong> newsletter (3 tips/week). Unsubscribe anytime.
            </p>
          </form>

          <div className="grid sm:grid-cols-3 gap-3 mt-8">
            {[
              { Icon: ShieldCheck, label: 'No credentials needed' },
              { Icon: FileText, label: 'Full action plan' },
              { Icon: Inbox, label: 'Emailed instantly' },
            ].map(({ Icon, label }) => (
              <div key={label} className="flex items-center gap-2 px-4 py-3 rounded-lg bg-slate-800/30 border border-slate-700/40">
                <Icon className="w-4 h-4 text-emerald-400" />
                <span className="text-xs text-slate-300">{label}</span>
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  // ─── SENDING ────────────────────────────────────────────────────────────────
  if (step === QUESTIONS.length && submitting) {
    return (
      <div className="min-h-screen bg-slate-950 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white flex items-center justify-center px-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-400 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Generating your audit…</h2>
          <p className="text-slate-400 text-sm">Analyzing your responses against 8 waste patterns.</p>
        </div>
      </div>
    );
  }

  // ─── RESULT (post-submission) ───────────────────────────────────────────────
  if (step >= QUESTIONS.length + 1 && result) {
    return (
      <div className="min-h-screen bg-slate-950 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
        <Header />
        <main className="max-w-3xl mx-auto px-4 py-12 sm:py-16" data-testid="audit-tool-result">
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-6 mb-8 flex items-start gap-4">
            <CheckCircle className="w-7 h-7 text-emerald-400 shrink-0 mt-1" />
            <div>
              <h1 className="text-2xl font-bold mb-2">
                {result.email_sent ? "Your audit is on its way." : "Your audit is ready."}
              </h1>
              <p className="text-slate-300">
                {result.email_sent
                  ? <>The full report has been emailed to <strong className="text-white">{profile.email}</strong>. Check your inbox in the next minute or two.</>
                  : <>We saved your audit but couldn't send the email. The full report is below.</>
                }
              </p>
            </div>
          </div>

          {/* Quick summary */}
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-5" data-testid="audit-tool-score">
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Your Score</p>
              <p className="text-4xl font-extrabold text-emerald-400">{result.score}<span className="text-base text-slate-400 font-medium">/100</span></p>
              <p className="text-sm text-slate-300 mt-1">Grade: <strong className="text-white">{result.grade}</strong></p>
            </div>
            <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-5">
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Monthly Waste</p>
              <p className="text-4xl font-extrabold text-rose-400">${result.estimated_monthly_waste_usd.toLocaleString()}</p>
              <p className="text-sm text-slate-400 mt-1">${result.estimated_annual_waste_usd.toLocaleString()}/year</p>
            </div>
            <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-5">
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Findings</p>
              <p className="text-4xl font-extrabold text-amber-400">{result.total_findings}</p>
              <p className="text-sm text-slate-400 mt-1">issues identified</p>
            </div>
          </div>

          <p className="text-slate-300 mb-4 text-sm">{result.verdict}</p>

          <h2 className="text-lg font-semibold mb-3 mt-8">Your full audit findings</h2>
          <div className="space-y-3">
            {(result.findings || []).map((f, i) => {
              const sev = { 3: { label: 'HIGH', color: 'bg-rose-500/20 border-rose-500/30 text-rose-300' },
                            2: { label: 'MEDIUM', color: 'bg-amber-500/20 border-amber-500/30 text-amber-300' },
                            1: { label: 'LOW', color: 'bg-emerald-500/20 border-sky-500/30 text-sky-300' } }[f.severity]
                            || { label: 'INFO', color: 'bg-slate-500/20 border-slate-500/30 text-slate-300' };
              return (
                <div key={i} className="bg-slate-800/40 border border-slate-700 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[11px] font-bold tracking-wider px-2 py-0.5 rounded border ${sev.color}`}>
                      #{i + 1} · {sev.label}
                    </span>
                    <span className="text-xs text-slate-400">~{f.savings_pct_estimate}% potential saving</span>
                  </div>
                  <p className="font-semibold text-white mb-1">{f.title}</p>
                  <p className="text-sm text-slate-300 leading-relaxed">{f.fix}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-12 bg-gradient-to-br from-emerald-500/15 to-cyan-500/10 border border-emerald-500/20 rounded-2xl p-6 text-center">
            <Sparkles className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
            <h3 className="text-xl font-bold mb-2">Want this fixed for you?</h3>
            <p className="text-slate-300 max-w-lg mx-auto mb-4 text-sm">
              Book a free 15-minute call. We'll walk through your top findings and tell you exactly which fixes deliver the fastest ROI.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950" data-testid="audit-tool-book-cta">
                Book a 15-min call
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // ─── QUIZ STEP ──────────────────────────────────────────────────────────────
  const q = QUESTIONS[step];
  const selected = answers[q.key];
  return (
    <div className="min-h-screen bg-slate-950 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <Header />

      {/* Progress */}
      <div className="h-1 bg-white/5">
        <div className="h-full bg-emerald-500 transition-all duration-300" style={{ width: `${progressPct}%` }} />
      </div>

      <main className="max-w-2xl mx-auto px-4 py-10 sm:py-14" data-testid="audit-tool-question">
        <div className="flex items-center justify-between mb-6">
          <button onClick={onBack} className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1" data-testid="audit-tool-back">
            <ArrowLeft className="w-3 h-3" /> Back
          </button>
          <span className="text-xs text-slate-400">Question {step + 1} of {QUESTIONS.length}</span>
        </div>

        <h2 className="text-2xl sm:text-3xl font-bold mb-2 leading-snug">{q.title}</h2>
        <p className="text-sm text-slate-400 mb-8">{q.sub}</p>

        <div className="space-y-2">
          {q.options.map((opt) => {
            const isSelected = selected === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => onPick(q.key, opt.value)}
                className={`w-full text-left px-4 py-3.5 rounded-xl border transition-all ${
                  isSelected
                    ? 'bg-emerald-500/15 border-emerald-500 text-white'
                    : 'bg-slate-800/40 border-slate-700 text-slate-200 hover:border-emerald-500/40 hover:bg-slate-800/70'
                }`}
                data-testid={`audit-tool-option-${q.key}-${opt.value}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm sm:text-base">{opt.label}</span>
                  <ArrowRight className={`w-4 h-4 transition-transform ${isSelected ? 'text-emerald-400 translate-x-1' : 'text-slate-400'}`} />
                </div>
              </button>
            );
          })}
        </div>

        {step === QUESTIONS.length - 1 && (
          <p className="text-xs text-slate-400 text-center mt-8 inline-flex items-center gap-1.5 justify-center w-full">
            <AlertTriangle className="w-3 h-3" />
            Your audit will be emailed to <strong className="text-slate-300">{profile.email}</strong> after this question.
          </p>
        )}
      </main>
    </div>
  );
};

const Header = () => (
  <header className="px-4 py-5 border-b border-white/5">
    <div className="max-w-5xl mx-auto flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2">
        <Snowflake className="w-6 h-6 text-emerald-400" />
        <span className="font-bold">CARTIE<span className="text-emerald-400">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
      </Link>
      <Link to="/snowflake-health-score" className="text-xs text-slate-400 hover:text-white">
        Try the quick 10-Q version →
      </Link>
    </div>
  </header>
);

export default SnowflakeAuditTool;
